import clsx from 'clsx'
import type { Dispatch, PropsWithChildren, ReactElement } from 'react'
import React, {
  Children,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import Collapse from '../Collapse/Collapse'
import { useElementOverflow } from '../hooks/useElementOverflow'
import { useId } from '../hooks/useId'
import IconButton from '../IconButton/IconButton'
import ExpandLess from '../icons/ExpandLess'
import ExpandMore from '../icons/ExpandMore'
import type { Actions } from './Actions'
import { ActionTypes } from './Actions'

interface ActionProps {
  children: JSX.Element
}

export interface CardProps {
  outlined?: boolean
}

interface HeaderProps {
  children: ReactElement | ReactElement[]
}

interface CardState {
  collapsable: boolean
  collapseId: string | undefined
  hasActions: boolean
  open: boolean
}

type CardContextValues = [CardState, Dispatch<Actions>]

type CardReducers = {
  [ActionType in ActionTypes]: (state: CardState, action: Actions) => CardState
}

const CardContext = createContext<CardContextValues | null>(null)

const useCard = () => useContext(CardContext) as CardContextValues

function createInitialState(): CardState {
  return {
    collapsable: false,
    collapseId: undefined,
    hasActions: false,
    open: false,
  }
}

const reducers: CardReducers = {
  [ActionTypes.SetCollapsable]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SetCollapsable }>,
  ) => {
    return { ...state, collapsable: true, collapseId: action.collapseId }
  },
  [ActionTypes.SetHasActions]: (state) => {
    return { ...state, hasActions: true }
  },
  [ActionTypes.UnsetCollapsable]: (state) => {
    return { ...state, collapsable: false, collapseId: undefined }
  },
  [ActionTypes.UnsetHasActions]: (state) => {
    return { ...state, hasActions: false }
  },
  [ActionTypes.ToggleOpen]: (state) => {
    return { ...state, open: !state.open }
  },
}

const runReducer = (state: CardState, action: Actions): CardState =>
  reducers[action.type](state, action)

function Action({ children }: ActionProps): JSX.Element {
  return children
}

function CardActions({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const [, dispatch] = useCard()

  useEffect(() => {
    dispatch({ type: ActionTypes.SetHasActions })

    return () => dispatch({ type: ActionTypes.UnsetHasActions })
  }, [dispatch])

  return <div className="shrink-0 p-2 space-x-2">{children}</div>
}

function Card({
  children,
  outlined = false,
}: PropsWithChildren<CardProps>): JSX.Element {
  const [state, dispatch] = useReducer(runReducer, null, createInitialState)

  return (
    <div
      className={clsx(
        'flex flex-col w-full h-full font-sans bg-white-100 rounded max-h-inherit',
        outlined ? 'border border-black-15' : 'shadow-1',
      )}
    >
      <CardContext.Provider value={[state, dispatch]}>
        {children}
      </CardContext.Provider>
    </div>
  )
}

function CollapseButton(): JSX.Element {
  const intl = useIntl()
  const [{ open, collapseId }, dispatch] = useCard()

  const onClick = () => {
    dispatch({ type: ActionTypes.ToggleOpen })
  }

  return (
    <IconButton
      aria-controls={collapseId}
      aria-expanded={open}
      aria-label={intl.formatMessage({ id: 'Card.toggle_content' })}
      onClick={onClick}
      size="medium"
    >
      {open ? <ExpandLess /> : <ExpandMore />}
    </IconButton>
  )
}

function CollapseContent({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const [{ open }, dispatch] = useCard()
  const collapseId = `coconut-collapsable-card-content-${useId()}`
  useEffect(() => {
    dispatch({ type: ActionTypes.SetCollapsable, collapseId: collapseId })

    return () => dispatch({ type: ActionTypes.UnsetCollapsable })
  }, [dispatch, collapseId])

  return (
    <Collapse id={collapseId} open={open}>
      {children}
    </Collapse>
  )
}

function Content({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const [{ hasActions, open }] = useCard()
  const ref = useRef<HTMLDivElement | null>(null)
  const [rendering, setRendering] = useState(false)
  const overflowing = useElementOverflow(ref, rendering)

  useEffect(() => {
    setRendering(true)
  }, [children, open])

  useLayoutEffect(() => {
    setRendering(false)
  }, [children, open, ref])

  return (
    <div
      className={clsx(
        'py-4 overflow-hidden overflow-y-auto',
        overflowing ? 'border-t border-black-10' : null,
        overflowing && hasActions ? 'border-b' : null,
      )}
      ref={ref}
    >
      <div className="px-4">{children}</div>
    </div>
  )
}

function Header({ children }: HeaderProps): JSX.Element {
  const [{ collapsable }] = useCard()
  const components: Record<string, ReactElement | null> = {
    action: null,
    subtitle: null,
    title: null,
  }

  // TODO: This is written like this because Typescript complains when trying to access `child.type.name`. We need to figure out a way to type the child appropriately.
  Children.forEach(children, (child) => {
    switch (child.type) {
      case Action:
        components.action = child
        break
      case Subtitle:
        components.subtitle = child
        break
      case Title:
        components.title = child
        break
    }
  })

  return (
    <div className="flex flex-row items-center justify-between shrink-0 p-4">
      {components.title || components.subtitle ? (
        <div className="flex flex-col">
          {components.title}
          {components.subtitle}
        </div>
      ) : null}
      <div>
        {collapsable ? <CollapseButton /> : null}
        {components.action}
      </div>
    </div>
  )
}

function Subtitle({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return (
    <span className="text-sm font-normal leading-5 text-black-60">
      {children}
    </span>
  )
}

function Title({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  return (
    <span className="text-sm font-normal leading-5 text-black-90">
      {children}
    </span>
  )
}

Card.Actions = CardActions
Card.Action = Action
Card.Collapse = CollapseContent
Card.Content = Content
Card.Header = Header
Card.Subtitle = Subtitle
Card.Title = Title

export default Card
